import isDeepEqual from 'fast-deep-equal';
import { TopicLayerSettings } from '@agissc/ktag-map';
import { IAppStore } from '../IAppStore';
import { FlyinNames } from '../UIStateSlice';
import { SubLayerProps } from '../tools/mapContent/api/Contracts';
import { MapContentViews } from '../tools/MapContentViews';

const createIdRanges = (ids: number[]): string => {
  if (ids.length === 0) return '';

  let result = '';
  let startRange = ids[0];
  let lastId = ids[0];

  for (let i = 1; i <= ids.length; i += 1) {
    const currentId = ids[i];

    if (currentId === lastId + 1) {
      lastId = currentId;
    } else {
      if (startRange === lastId) {
        result += `${startRange}`;
      } else {
        result += `${startRange}-${lastId}`;
      }

      if (i < ids.length) {
        result += ',';
      }

      startRange = currentId;
      lastId = currentId;
    }
  }

  return result;
};

const processTopicLayers = (
  selectedTopicLayers: string[],
  topicLayerSettings: Record<string, TopicLayerSettings>,
  topicSubLayerSetting: Record<string, SubLayerProps[]>,
) => {
  return selectedTopicLayers
    .map((layer) => {
      const { opacity = 1, isVisible } = topicLayerSettings[layer];
      const enabledIds = (topicSubLayerSetting[layer] ?? [])
        .filter(({ isEnabled }) => isEnabled)
        .map(({ id }) => id);

      const ids = createIdRanges(enabledIds);

      return [layer, opacity, String(isVisible), ids].filter(Boolean).join(';');
    })
    .join('|');
};

const setUrlParams = (oldvalue: string, newvalue: string) => {
  const searchParams = new URLSearchParams(window.location.search);
  const setUrlParam = (key: string, value: string) => searchParams.set(key, value);

  const currentState = JSON.parse(newvalue).state as IAppStore;
  const prevState =
    (JSON.parse(oldvalue).state as IAppStore) ?? ({} as Partial<IAppStore>);

  if (
    !isDeepEqual(currentState.selectedTopicLayers, prevState.selectedTopicLayers) ||
    !isDeepEqual(currentState.topicLayerSettings, prevState.topicLayerSettings) ||
    !isDeepEqual(currentState.topicSubLayerSetting, prevState.topicSubLayerSetting) ||
    searchParams.get('layers') === null
  ) {
    const layers = processTopicLayers(
      currentState.selectedTopicLayers,
      currentState.topicLayerSettings,
      currentState.topicSubLayerSetting,
    );

    if (layers.length === 0) {
      searchParams.delete('layers');
    } else {
      setUrlParam('layers', layers);
    }
  }

  const basemapOpacity = currentState.basemapOpacity ?? 1;

  setUrlParam(
    'basemap',
    `${currentState.selectedBasemapId},${basemapOpacity},${currentState.isBasemapVisible}`,
  );

  if (currentState.location.center) {
    const x = currentState.location.center.x.toFixed(2);
    const y = currentState.location.center.y.toFixed(2);
    setUrlParam('center', `${x},${y}`);
    setUrlParam('z', `${currentState.location.zoom}`);
  }

  if (currentState.activeFlyin === FlyinNames.Legends) {
    setUrlParam('legend', '');
    searchParams.delete('info');
  } else if (currentState.identifyCoordinate) {
    setUrlParam(
      'info',
      `${currentState.identifyCoordinate.join(',')},${currentState.tolerance}`,
    );
    searchParams.delete('legend');
  } else {
    searchParams.delete('info');
    searchParams.delete('legend');
  }

  if (currentState.selectedSearchResult) {
    if (!isDeepEqual(currentState.selectedSearchResult, prevState.selectedSearchResult)) {
      const { detail, origin } = currentState.selectedSearchResult;
      setUrlParam('search', `${detail},${origin}`);
    }
  } else {
    searchParams.delete('search');
  }

  const { activeView, attributeSearch, topicLayerSettings } = currentState;

  if (
    currentState.currentAttributeSearchTopicId &&
    attributeSearch[currentState.currentAttributeSearchTopicId]
  ) {
    const { sublayerId, attribute, searchText, draftQuery } =
      attributeSearch[currentState.currentAttributeSearchTopicId];
    const viewVisible = activeView === MapContentViews.topicAttributeSearch;
    setUrlParam(
      'query',
      `${currentState.currentAttributeSearchTopicId},${sublayerId},${attribute},${searchText},${draftQuery},${viewVisible}`,
    );

    if (
      topicLayerSettings[currentState.currentAttributeSearchTopicId]?.isPublic === false
    ) {
      setUrlParam('forceauth', '');
    } else {
      searchParams.delete('forceauth');
    }
  } else {
    searchParams.delete('query');
    searchParams.delete('forceauth');
  }

  if (currentState.displayStartUpMessage) {
    setUrlParam('welcome', '');
  } else {
    searchParams.delete('welcome');
  }

  const queryString = searchParams
    .toString()
    .replace(/%3A/g, ':')
    .replace(/%2C/g, ',')
    .replace(/%3B/g, ';')
    .replace(/%7C/g, '|');

  const queryParam = `?${queryString}`;

  if (queryParam !== window.location.search) {
    const newUrl = `${window.location.origin}${window.location.pathname}${queryParam}`;
    window.history.replaceState(null, '', newUrl);
  }
};

export default setUrlParams;
